import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './UniversityArchive.module.scss'
import { 
  getUniversityFunds, 
  getUniversityInventories,
  createUniversityInventory,
  createUniversityFund, 
  updateUniversityFund,
  deleteUniversityFund,
  getUniversityCases,
  createUniversityCase,
  createUniversityDocument,
  uploadUniversityDocumentFile
} from '../../../service/ArchiveService'
import { ScaleLoader } from 'react-spinners'
import Notification from '../../../utils/Notifications'

function UniversityArchive() {
  const navigate = useNavigate()
  const [funds, setFunds] = useState([])
  const [inventories, setInventories] = useState([])
  const [cases, setCases] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedFund, setSelectedFund] = useState(null)
  const [selectedInventory, setSelectedInventory] = useState(null)
  const [showCreateFundForm, setShowCreateFundForm] = useState(false)
  const [showCreateInventoryForm, setShowCreateInventoryForm] = useState(false)
  const [showCreateCaseForm, setShowCreateCaseForm] = useState(false)
  const [showCreateDocForm, setShowCreateDocForm] = useState(false)
  const [selectedCase, setSelectedCase] = useState(null)
  const [fundFormData, setFundFormData] = useState({
    fund_number: '',
    title: '',
    description: ''
  })
  const [inventoryFormData, setInventoryFormData] = useState({
    fund: '',
    inventory_number: '',
    title: '',
    description: ''
  })
  const [caseFormData, setCaseFormData] = useState({
    inventory: '',
    case_number: '',
    title: '',
    description: '',
    case_date_start: '',
    case_date_end: '',
    storage_term: 75,
    physical_location: '',
    status: 'active'
  })
  const [documentData, setDocumentData] = useState({
    case: null,
    document_type: 'order',
    title: '',
    description: '',
    document_date: '',
    pages: 0,
    confidentiality_level: 'public',
    notes: ''
  })
  const [uploadFile, setUploadFile] = useState(null)
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '', sound: '' })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [fundsData, inventoriesData, casesData] = await Promise.all([
        getUniversityFunds(),
        getUniversityInventories(),
        getUniversityCases()
      ])
      setFunds(fundsData)
      setInventories(inventoriesData)
      setCases(casesData)
    } catch (error) {
      console.error('Ошибка при загрузке данных:', error)
      setNotify({
        isOpen: true,
        message: 'Ошибка при загрузке данных',
        type: 'error',
        sound: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCreateFund = async (e) => {
    e.preventDefault()
    try {
      const newFund = await createUniversityFund(fundFormData)
      setFunds([...funds, newFund])
      setFundFormData({ fund_number: '', title: '', description: '' })
      setShowCreateFundForm(false)
      setNotify({
        isOpen: true,
        message: 'Фонд успешно создан!',
        type: 'success',
        sound: 'success',
      })
    } catch (error) {
      console.error('Ошибка при создании фонда:', error)
      setNotify({
        isOpen: true,
        message: 'Ошибка при создании фонда',
        type: 'error',
        sound: 'error',
      })
    }
  }

  const handleUpdateFund = async (fundId, updates) => {
    try {
      const updatedFund = await updateUniversityFund(fundId, updates)
      setFunds(funds.map(f => f.id === fundId ? updatedFund : f))
      setNotify({
        isOpen: true,
        message: 'Фонд успешно обновлен!',
        type: 'success',
        sound: 'success',
      })
    } catch (error) {
      console.error('Ошибка при обновлении фонда:', error)
      setNotify({
        isOpen: true,
        message: 'Ошибка при обновлении фонда',
        type: 'error',
        sound: 'error',
      })
    }
  }

  const handleDeleteFund = async (fundId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот фонд?')) {
      return
    }
    try {
      await deleteUniversityFund(fundId)
      setFunds(funds.filter(f => f.id !== fundId))
      setNotify({
        isOpen: true,
        message: 'Фонд успешно удален!',
        type: 'success',
        sound: 'success',
      })
    } catch (error) {
      console.error('Ошибка при удалении фонда:', error)
      setNotify({
        isOpen: true,
        message: 'Ошибка при удалении фонда',
        type: 'error',
        sound: 'error',
      })
    }
  }

  const handleCreateInventory = async (e) => {
    e.preventDefault()
    try {
      const newInventory = await createUniversityInventory(inventoryFormData)
      setInventories([...inventories, newInventory])
      setInventoryFormData({ fund: '', inventory_number: '', title: '', description: '' })
      setShowCreateInventoryForm(false)
      setNotify({
        isOpen: true,
        message: 'Опись успешно создана!',
        type: 'success',
        sound: 'success',
      })
      // Обновляем данные для актуализации связи с фондом
      loadData()
    } catch (error) {
      console.error('Ошибка при создании описи:', error)
      setNotify({
        isOpen: true,
        message: 'Ошибка при создании описи',
        type: 'error',
        sound: 'error',
      })
    }
  }

  const handleCreateCase = async (e) => {
    e.preventDefault()
    try {
      const newCase = await createUniversityCase(caseFormData)
      setCases([...cases, newCase])
      setCaseFormData({
        inventory: '',
        case_number: '',
        title: '',
        description: '',
        case_date_start: '',
        case_date_end: '',
        storage_term: 75,
        physical_location: '',
        status: 'active'
      })
      setShowCreateCaseForm(false)
      setNotify({
        isOpen: true,
        message: 'Дело успешно создано!',
        type: 'success',
        sound: 'success',
      })
    } catch (error) {
      console.error('Ошибка при создании дела:', error)
      setNotify({
        isOpen: true,
        message: 'Ошибка при создании дела',
        type: 'error',
        sound: 'error',
      })
    }
  }

  const handleCreateDocument = async (e) => {
    e.preventDefault()
    try {
      const docData = {
        ...documentData,
        case: selectedCase.id
      }
      // Создаем документ с файлом сразу
      const newDocument = await createUniversityDocument(docData, uploadFile)
      
      // Обновляем выбранное дело
      const updatedCases = cases.map(c => 
        c.id === selectedCase.id 
          ? { ...c, documents: [...(c.documents || []), newDocument] }
          : c
      )
      setCases(updatedCases)
      setSelectedCase({ ...selectedCase, documents: [...(selectedCase.documents || []), newDocument] })
      
      setDocumentData({
        case: null,
        document_type: 'order',
        title: '',
        description: '',
        document_date: '',
        pages: 0,
        confidentiality_level: 'public',
        notes: ''
      })
      setUploadFile(null)
      setShowCreateDocForm(false)
      setNotify({
        isOpen: true,
        message: 'Документ успешно добавлен!',
        type: 'success',
        sound: 'success',
      })
    } catch (error) {
      console.error('Ошибка при создании документа:', error)
      setNotify({
        isOpen: true,
        message: 'Ошибка при создании документа',
        type: 'error',
        sound: 'error',
      })
    }
  }

  const documentTypes = [
    { value: 'order', label: 'Приказ' },
    { value: 'protocol', label: 'Протокол' },
    { value: 'report', label: 'Отчёт' },
    { value: 'contract', label: 'Договор' },
    { value: 'study_plan', label: 'Учебный план' },
    { value: 'correspondence', label: 'Переписка' },
    { value: 'other', label: 'Прочее' },
  ]

  const getDocumentTypeLabel = (value) => {
    const type = documentTypes.find(t => t.value === value)
    return type ? type.label : value
  }

  const getConfidentialityLabel = (value) => {
    const levels = {
      'public': 'Открытый',
      'internal': 'Внутренний',
      'confidential': 'Конфиденциальный',
      'secret': 'Секретный'
    }
    return levels[value] || value
  }

  if (loading) {
    return (
      <div className={styles.loading_container}>
        <ScaleLoader color="#0066cc" />
      </div>
    )
  }

  return (
    <>
      <Notification notify={notify} setNotify={setNotify} />
      <div className={styles.university_archive}>
      <div className={styles.header}>
        <h1>Архив университета</h1>
      </div>

      {showCreateFundForm && (
        <div className={styles.modal}>
          <div className={styles.modal_content}>
            <button 
              className={styles.close_btn}
              onClick={() => setShowCreateFundForm(false)}
              type="button"
            >
              ×
            </button>
            <h2>Создание нового фонда</h2>
            <form onSubmit={handleCreateFund}>
              <div className={styles.form_field}>
                <label>Номер фонда *</label>
                <input
                  type="text"
                  placeholder="Введите номер фонда"
                  required
                  value={fundFormData.fund_number}
                  onChange={(e) => setFundFormData({ ...fundFormData, fund_number: e.target.value })}
                />
              </div>
              <div className={styles.form_field}>
                <label>Название *</label>
                <input
                  type="text"
                  placeholder="Введите название"
                  required
                  value={fundFormData.title}
                  onChange={(e) => setFundFormData({ ...fundFormData, title: e.target.value })}
                />
              </div>
              <div className={styles.form_field}>
                <label>Описание</label>
                <textarea
                  placeholder="Укажите описание фонда"
                  value={fundFormData.description}
                  onChange={(e) => setFundFormData({ ...fundFormData, description: e.target.value })}
                />
              </div>
              <div className={styles.form_actions}>
                <button type="submit" className={styles.btn_save}>
                  Сохранить
                </button>
                <button
                  type="button"
                  className={styles.btn_cancel}
                  onClick={() => setShowCreateFundForm(false)}
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreateInventoryForm && (
        <div className={styles.modal}>
          <div className={styles.modal_content}>
            <button 
              className={styles.close_btn}
              onClick={() => setShowCreateInventoryForm(false)}
              type="button"
            >
              ×
            </button>
            <h2>Создание новой описи</h2>
            <form onSubmit={handleCreateInventory}>
              <div className={styles.form_field}>
                <label>Фонд</label>
                <input
                  type="text"
                  disabled
                  value={selectedFund ? `${selectedFund.fund_number} - ${selectedFund.title}` : ''}
                />
              </div>
              <div className={styles.form_field}>
                <label>Номер описи *</label>
                <input
                  type="text"
                  placeholder="Введите номер описи"
                  required
                  value={inventoryFormData.inventory_number}
                  onChange={(e) => setInventoryFormData({ ...inventoryFormData, inventory_number: e.target.value })}
                />
              </div>
              <div className={styles.form_field}>
                <label>Название *</label>
                <input
                  type="text"
                  placeholder="Введите название"
                  required
                  value={inventoryFormData.title}
                  onChange={(e) => setInventoryFormData({ ...inventoryFormData, title: e.target.value })}
                />
              </div>
              <div className={styles.form_field}>
                <label>Описание</label>
                <textarea
                  placeholder="Укажите описание описи"
                  value={inventoryFormData.description}
                  onChange={(e) => setInventoryFormData({ ...inventoryFormData, description: e.target.value })}
                />
              </div>
              <div className={styles.form_actions}>
                <button type="submit" className={styles.btn_save}>
                  Сохранить
                </button>
                <button
                  type="button"
                  className={styles.btn_cancel}
                  onClick={() => setShowCreateInventoryForm(false)}
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreateCaseForm && (
        <div className={styles.modal}>
          <div className={styles.modal_content}>
            <button 
              className={styles.close_btn}
              onClick={() => setShowCreateCaseForm(false)}
              type="button"
            >
              ×
            </button>
            <h2>Создание нового дела</h2>
            <form onSubmit={handleCreateCase}>
              <div className={styles.form_field}>
                <label>Опись</label>
                <input
                  type="text"
                  disabled
                  value={selectedInventory ? `${selectedInventory.inventory_number} - ${selectedInventory.title}` : ''}
                />
              </div>
              <div className={styles.form_field}>
                <label>Номер дела *</label>
                <input
                  type="text"
                  placeholder="Введите номер дела"
                  required
                  value={caseFormData.case_number}
                  onChange={(e) => setCaseFormData({ ...caseFormData, case_number: e.target.value })}
                />
              </div>
              <div className={styles.form_field}>
                <label>Название *</label>
                <input
                  type="text"
                  placeholder="Введите название"
                  required
                  value={caseFormData.title}
                  onChange={(e) => setCaseFormData({ ...caseFormData, title: e.target.value })}
                />
              </div>
              <div className={styles.form_field}>
                <label>Описание</label>
                <textarea
                  placeholder="Укажите описание дела"
                  value={caseFormData.description}
                  onChange={(e) => setCaseFormData({ ...caseFormData, description: e.target.value })}
                />
              </div>
              <div className={styles.form_row}>
                <div className={styles.form_field}>
                  <label>Начало периода</label>
                  <input
                    type="date"
                    value={caseFormData.case_date_start}
                    onChange={(e) => setCaseFormData({ ...caseFormData, case_date_start: e.target.value })}
                  />
                </div>
                <div className={styles.form_field}>
                  <label>Окончание периода</label>
                  <input
                    type="date"
                    value={caseFormData.case_date_end}
                    onChange={(e) => setCaseFormData({ ...caseFormData, case_date_end: e.target.value })}
                  />
                </div>
              </div>
              <div className={styles.form_field}>
                <label>Срок хранения (лет)</label>
                <input
                  type="number"
                  placeholder="75"
                  value={caseFormData.storage_term}
                  onChange={(e) => setCaseFormData({ ...caseFormData, storage_term: parseInt(e.target.value) })}
                />
              </div>
              <div className={styles.form_field}>
                <label>Физическое расположение</label>
                <input
                  type="text"
                  placeholder="Например, Архив, стеллаж 1, полка 3"
                  value={caseFormData.physical_location}
                  onChange={(e) => setCaseFormData({ ...caseFormData, physical_location: e.target.value })}
                />
              </div>
              <div className={styles.form_actions}>
                <button type="submit" className={styles.btn_save}>
                  Создать
                </button>
                <button
                  type="button"
                  className={styles.btn_cancel}
                  onClick={() => setShowCreateCaseForm(false)}
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className={styles.content}>
        {/* Секция фондов */}
        <div className={styles.section}>
          <div className={styles.section_header}>
            <h2>Фонды ({funds.length})</h2>
            <button
              className={styles.btn_create_small}
              onClick={() => setShowCreateFundForm(!showCreateFundForm)}
            >
              + Создать
            </button>
          </div>
          <div className={styles.funds_list}>
            {funds.map((fund) => (
              <div 
                key={fund.id} 
                className={`${styles.fund_card} ${selectedFund?.id === fund.id ? styles.active : ''}`}
                onClick={() => {
                  setSelectedFund(fund)
                  setSelectedInventory(null)
                  setSelectedCase(null)
                }}
              >
                <div className={styles.fund_header}>
                  <h4>{fund.fund_number}</h4>
                </div>
                <p className={styles.fund_title}>{fund.title}</p>
                <p className={styles.fund_desc}>{fund.description}</p>
                <small>Описей: {inventories.filter(inv => inv.fund === fund.id).length}</small>
              </div>
            ))}
          </div>
        </div>

        {/* Секция описей */}
        {selectedFund && (
          <div className={styles.section}>
            <div className={styles.section_header}>
              <h2>Описи фонда "{selectedFund.fund_number}"</h2>
              <button
                className={styles.btn_create_small}
                onClick={() => {
                  setInventoryFormData({ ...inventoryFormData, fund: selectedFund.id })
                  setShowCreateInventoryForm(!showCreateInventoryForm)
                }}
              >
                + Создать
              </button>
            </div>
            <div className={styles.inventories_list}>
              {inventories
                .filter(inv => inv.fund === selectedFund.id)
                .map((inventory) => (
                  <div
                    key={inventory.id}
                    className={`${styles.inventory_item} ${selectedInventory?.id === inventory.id ? styles.active : ''}`}
                    onClick={() => {
                      setSelectedInventory(inventory)
                      setSelectedCase(null)
                    }}
                  >
                    <div className={styles.inventory_number}>{inventory.inventory_number}</div>
                    <div className={styles.inventory_details}>
                      <p className={styles.inventory_title}>{inventory.title}</p>
                      {inventory.description && (
                        <p className={styles.inventory_desc}>{inventory.description}</p>
                      )}
                      <small>Дел: {cases.filter(c => c.inventory === inventory.id).length}</small>
                    </div>
                  </div>
                ))}
              {inventories.filter(inv => inv.fund === selectedFund.id).length === 0 && (
                <p className={styles.empty_message}>Нет описей. Создайте первую опись.</p>
              )}
            </div>
          </div>
        )}

        {/* Секция дел */}
        {selectedInventory && (
          <div className={styles.section}>
            <div className={styles.section_header}>
              <h2>Дела описи "{selectedInventory.inventory_number}"</h2>
              <button
                className={styles.btn_create_small}
                onClick={() => {
                  setCaseFormData({ ...caseFormData, inventory: selectedInventory.id })
                  setShowCreateCaseForm(!showCreateCaseForm)
                }}
              >
                + Создать
              </button>
            </div>
            <div className={styles.cases_list}>
              {cases
                .filter(c => c.inventory === selectedInventory.id)
                .map((caseItem) => (
                  <div
                    key={caseItem.id}
                    className={`${styles.case_item} ${selectedCase?.id === caseItem.id ? styles.active : ''}`}
                    onClick={() => setSelectedCase(caseItem)}
                  >
                    <div className={styles.case_number}>{caseItem.case_number}</div>
                    <div className={styles.case_details}>
                      <p className={styles.case_title}>{caseItem.title}</p>
                      <p className={styles.case_status}>Статус: {caseItem.status}</p>
                      {caseItem.is_digitized && (
                        <span className={styles.digitized_badge}>✓ Оцифровано</span>
                      )}
                    </div>
                  </div>
                ))}
              {cases.filter(c => c.inventory === selectedInventory.id).length === 0 && (
                <p className={styles.empty_message}>Нет дел. Создайте первое дело.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {selectedCase && (
          <div className={styles.details_panel}>
            <div className={styles.panel_header}>
              <h2>Документы в деле</h2>
              <button
                className={styles.btn_add_doc}
                onClick={() => setShowCreateDocForm(!showCreateDocForm)}
              >
                + Добавить документ
              </button>
            </div>

            {showCreateDocForm && (
              <div className={styles.document_form}>
                <h3>Добавление документа</h3>
                <form onSubmit={handleCreateDocument}>
                  <div className={styles.form_field}>
                    <label>Тип документа *</label>
                    <select
                      value={documentData.document_type}
                      onChange={(e) => setDocumentData({ ...documentData, document_type: e.target.value })}
                      required
                    >
                      {documentTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.form_field}>
                    <label>Название документа *</label>
                    <input
                      type="text"
                      placeholder="Введите название"
                      required
                      value={documentData.title}
                      onChange={(e) => setDocumentData({ ...documentData, title: e.target.value })}
                    />
                  </div>
                  <div className={styles.form_field}>
                    <label>Описание</label>
                    <textarea
                      placeholder="Дополнительная информация"
                      value={documentData.description}
                      onChange={(e) => setDocumentData({ ...documentData, description: e.target.value })}
                    />
                  </div>
                  <div className={styles.form_field}>
                    <label>Дата документа *</label>
                    <input
                      type="date"
                      required
                      value={documentData.document_date}
                      onChange={(e) => setDocumentData({ ...documentData, document_date: e.target.value })}
                    />
                  </div>
                  <div className={styles.form_field}>
                    <label>Количество страниц</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={documentData.pages}
                      onChange={(e) => setDocumentData({ ...documentData, pages: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className={styles.form_field}>
                    <label>Уровень конфиденциальности</label>
                    <select
                      value={documentData.confidentiality_level}
                      onChange={(e) => setDocumentData({ ...documentData, confidentiality_level: e.target.value })}
                    >
                      <option value="public">Открытый</option>
                      <option value="internal">Внутренний</option>
                      <option value="confidential">Конфиденциальный</option>
                      <option value="secret">Секретный</option>
                    </select>
                  </div>
                  <div className={styles.form_field}>
                    <label>Примечания</label>
                    <textarea
                      placeholder="Дополнительные заметки"
                      value={documentData.notes}
                      onChange={(e) => setDocumentData({ ...documentData, notes: e.target.value })}
                    />
                  </div>
                  <div className={styles.form_field}>
                    <label>Файл документа</label>
                    <input
                      type="file"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                    />
                  </div>
                  <div className={styles.form_actions}>
                    <button type="submit" className={styles.btn_save}>
                      Добавить
                    </button>
                    <button
                      type="button"
                      className={styles.btn_cancel}
                      onClick={() => setShowCreateDocForm(false)}
                    >
                      Отмена
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className={styles.case_info}>
              <p>
                <strong>Дело:</strong> {selectedCase.case_number}
              </p>
              <p>
                <strong>Название:</strong> {selectedCase.title}
              </p>
              {selectedCase.case_date_start && selectedCase.case_date_end && (
                <p>
                  <strong>Период:</strong> {new Date(selectedCase.case_date_start).toLocaleDateString('ru-RU')} - {new Date(selectedCase.case_date_end).toLocaleDateString('ru-RU')}
                </p>
              )}
              <p>
                <strong>Срок хранения:</strong> {selectedCase.storage_term} лет
              </p>
            </div>

            <div className={styles.documents}>
              {selectedCase.documents && selectedCase.documents.length > 0 ? (
                <div className={styles.documents_list}>
                  {selectedCase.documents.map((doc) => (
                    <div key={doc.id} className={styles.document_item}>
                      <div className={styles.doc_type}>{getDocumentTypeLabel(doc.document_type)}</div>
                      <div className={styles.doc_info}>
                        <p className={styles.doc_title}>{doc.title}</p>
                        {doc.description && <p className={styles.doc_desc}>{doc.description}</p>}
                        <small>Дата: {new Date(doc.document_date).toLocaleDateString('ru-RU')}</small>
                        {doc.pages > 0 && <small> • Страниц: {doc.pages}</small>}
                        {doc.confidentiality_level && (
                          <small> • {getConfidentialityLabel(doc.confidentiality_level)}</small>
                        )}
                        {doc.files?.length > 0 && (
                          <div className={styles.doc_files}>
                            {doc.files.map((file) => (
                              <div key={file.id} className={styles.file_item}>
                                <a href={file.file} download className={styles.file_link}>
                                  📄 {file.file_name}
                                </a>
                                <a 
                                  href={file.file} 
                                  download={file.file_name}
                                  className={styles.btn_download}
                                  title="Скачать файл"
                                >
                                  ⬇️ Скачать
                                </a>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.no_documents}>Документов нет</p>
              )}
            </div>
          </div>
        )}
    </div>
    </>
  )
}

export default UniversityArchive
